import React, { useState } from 'react';
import { Upload, X, AlertCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { supabase } from '../../../lib/supabase';

interface UserImportModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

export function UserImport({ onClose, onSuccess }: UserImportModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [preview, setPreview] = useState<string[][]>([]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    // Validate file type
    if (!selectedFile.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    setFile(selectedFile);

    // Preview file contents
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split('\n');
      const headers = lines[0].split(',');
      
      // Validate required columns
      const requiredColumns = ['email', 'first_name', 'last_name'];
      const missingColumns = requiredColumns.filter(col => 
        !headers.map(h => h.trim().toLowerCase()).includes(col)
      );

      if (missingColumns.length > 0) {
        toast.error(`Missing required columns: ${missingColumns.join(', ')}`);
        setFile(null);
        return;
      }

      // Show preview of first 5 rows
      setPreview(
        lines.slice(0, 6).map(line => 
          line.split(',').map(cell => cell.trim())
        )
      );
    };
    reader.readAsText(selectedFile);
  };

  const processCSV = async (text: string) => {
    const lines = text.split('\n');
    const headers = lines[0].toLowerCase().split(',').map(h => h.trim());
    const users = lines.slice(1)
      .filter(line => line.trim())
      .map(line => {
        const values = line.split(',').map(v => v.trim());
        const user: Record<string, string> = {};
        headers.forEach((header, index) => {
          user[header] = values[index];
        });
        return user;
      });

    return users;
  };

  const handleImport = async () => {
    if (!file) return;

    setIsProcessing(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const text = e.target?.result as string;
        const users = await processCSV(text);

        // Import users in batches
        const batchSize = 50;
        const batches = [];
        for (let i = 0; i < users.length; i += batchSize) {
          batches.push(users.slice(i, i + batchSize));
        }

        let successCount = 0;
        let errorCount = 0;

        for (const batch of batches) {
          const results = await Promise.allSettled(
            batch.map(async (user) => {
              // Create auth user with random password
              const password = Math.random().toString(36).slice(-8);
              const { data: authData, error: authError } = await supabase.auth.signUp({
                email: user.email,
                password,
                options: {
                  data: {
                    first_name: user.first_name,
                    last_name: user.last_name
                  }
                }
              });

              if (authError) throw authError;

              // Create user profile
              const { error: profileError } = await supabase
                .from('user_profiles')
                .insert({
                  id: authData.user?.id,
                  first_name: user.first_name,
                  last_name: user.last_name,
                  is_admin: false,
                  is_active: true
                });

              if (profileError) throw profileError;

              return authData.user?.id;
            })
          );

          results.forEach(result => {
            if (result.status === 'fulfilled') successCount++;
            else errorCount++;
          });
        }

        toast.success(`Successfully imported ${successCount} users${errorCount > 0 ? ` (${errorCount} failed)` : ''}`);
        onSuccess();
      };
      reader.readAsText(file);
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Failed to import users');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-background-card rounded-xl max-w-2xl w-full">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-bold text-black">Import Users</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-300">
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3" />
              <div>
                <h3 className="text-sm font-medium text-yellow-800">CSV Format Requirements</h3>
                <ul className="mt-2 text-sm text-yellow-700 list-disc list-inside">
                  <li>Required columns: email, first_name, last_name</li>
                  <li>First row must be column headers</li>
                  <li>Email addresses must be unique</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="border-2 border-dashed border-gray-300 rounded-xl p-6">
            <input
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="hidden"
              id="csv-upload"
            />
            <label
              htmlFor="csv-upload"
              className="flex flex-col items-center cursor-pointer"
            >
              <Upload className="h-12 w-12 text-gray-400 mb-2" />
              <span className="text-sm text-gray-500">
                {file ? file.name : 'Click to upload CSV file'}
              </span>
            </label>
          </div>

          {preview.length > 0 && (
            <div className="overflow-x-auto">
              <h3 className="text-sm font-medium text-black mb-2">Preview</h3>
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    {preview[0].map((header, i) => (
                      <th
                        key={i}
                        className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {preview.slice(1).map((row, i) => (
                    <tr key={i}>
                      {row.map((cell, j) => (
                        <td key={j} className="px-3 py-2 text-sm text-black">
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-500"
            >
              Cancel
            </button>
            <button
              onClick={handleImport}
              disabled={!file || isProcessing}
              className="btn-primary"
            >
              {isProcessing ? 'Importing...' : 'Import Users'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}